/**
 * Куда перевести фокус, когда строка `removedKey` с фокусом ушла из списка:
 * первая уцелевшая строка после неё, а если таких нет — ближайшая уцелевшая
 * перед ней. Соседние ключи самой ушедшей монеты (она уходит всеми строками
 * разом) пропускаются: цель берётся из нового списка, а не из прежнего
 * соседства. `null` — строки не осталось или ключа не было в прежнем списке.
 */
export function focusTargetAfterRemoval(
  previousKeys: readonly string[],
  nextKeys: readonly string[],
  removedKey: string,
): string | null {
  const removedIndex = previousKeys.indexOf(removedKey);
  if (removedIndex < 0) {
    return null;
  }
  const alive = new Set(nextKeys);
  for (let index = removedIndex + 1; index < previousKeys.length; index += 1) {
    const key = previousKeys[index];
    if (key !== undefined && alive.has(key)) {
      return key;
    }
  }
  for (let index = removedIndex - 1; index >= 0; index -= 1) {
    const key = previousKeys[index];
    if (key !== undefined && alive.has(key)) {
      return key;
    }
  }
  return null;
}

/**
 * Перенос фокуса после скрытия монеты (§75 п. 2, design.md Д8). Браузер роняет
 * фокус на `body`, когда строка с фокусом пропадает из DOM, и клавиатурная
 * работа по списку обрывается. Но строки уходят и сами — терминальным
 * переходом на живом рынке, — и общее правило «ушла строка — двигай фокус»
 * дёргало бы экран к месту ушедшей строки. Поэтому переход взводится только
 * самим действием скрытия (хоткей, значок карточки, кнопка панели) и
 * срабатывает один раз.
 *
 * Состояние модульное, а не контекстное: действие скрытия живёт в экране, а
 * список строк — в хуке `useRowNavigation` на несколько уровней ниже; во
 * вкладке одновременно смонтирован один список строк.
 */

let armed = false;
let pendingKey: string | null = null;

/** Скрытие запущено: следующий уход строки под фокусом переносит фокус на соседа. */
export function armFocusHandoff(): void {
  armed = true;
}

/** Отказ скрытия или потерянный повод: переход больше не нужен. */
export function disarmFocusHandoff(): void {
  armed = false;
  pendingKey = null;
}

export function isFocusHandoffArmed(): boolean {
  return armed;
}

/** Цель, дождавшаяся закрытия панели: панель вернёт фокус сама, и цель берётся оттуда. */
export function setPendingHandoffKey(key: string): void {
  pendingKey = key;
}

/** Отдаёт отложенную цель один раз; переход на этом считается исполненным. */
export function takePendingHandoffKey(): string | null {
  const key = pendingKey;
  pendingKey = null;
  if (key !== null) {
    armed = false;
  }
  return key;
}

export type FocusHandoffPlan =
  | { kind: "idle" }
  | { kind: "drop" }
  | { kind: "focus"; key: string }
  | { kind: "defer"; key: string };

interface FocusHandoffInput {
  armed: boolean;
  /** Последняя строка, получившая фокус в этом списке. */
  focusedKey: string | null;
  previousKeys: readonly string[];
  keys: readonly string[];
  /** Поверх списка открыта панель: она держит фокус и вернёт его при закрытии. */
  dialogOpen: boolean;
}

/**
 * Что делать с переходом при смене состава строк — чистая часть, тестируемая
 * без DOM. `idle` — состав не менялся, взвод держится; `drop` — повод пропал
 * (фокус не стоял на строке, строка уцелела, соседей не осталось), взвод
 * снимается; `focus` — перенести сейчас; `defer` — перенести после закрытия
 * панели.
 */
export function planFocusHandoff(input: FocusHandoffInput): FocusHandoffPlan {
  if (!input.armed || input.keys === input.previousKeys) {
    return { kind: "idle" };
  }
  const { focusedKey } = input;
  if (focusedKey === null || input.keys.includes(focusedKey)) {
    return { kind: "drop" };
  }
  const target = focusTargetAfterRemoval(input.previousKeys, input.keys, focusedKey);
  if (target === null) {
    return { kind: "drop" };
  }
  return input.dialogOpen ? { kind: "defer", key: target } : { kind: "focus", key: target };
}

/**
 * Контейнеры списков с навигацией: `data-row-nav` у карточек и вида «По монете»,
 * `data-row-list` у таблицы и списка ситуаций. По ключам ходят карточки, «По
 * монете» и таблица, по DOM — только список ситуаций. Разные признаки, потому
 * что `data-row-nav` — маркер раскладки в прочих местах.
 */
export const NAV_CONTAINERS = "[data-row-nav], [data-row-list]";

/**
 * Строка навигации по ключу — внутри контейнеров списков, а не по всему
 * документу: в нём тот же `data-row-key` могут нести чужие элементы.
 */
export function findNavRow(key: string, root: ParentNode = document): HTMLElement | null {
  for (const container of root.querySelectorAll(NAV_CONTAINERS)) {
    const row = container.querySelector(`[data-row-key="${CSS.escape(key)}"]`);
    if (row instanceof HTMLElement) {
      return row;
    }
  }
  return null;
}

/**
 * Показать строку, которой нет в DOM: виртуальная таблица рисует только окно
 * прокрутки, и цель переноса может стоять за ним (design stage9-table-virtual
 * Д6). Список прокручивает к строке и сам фокусирует её, когда та нарисуется;
 * `false` — ключа в списке нет.
 */
export type RowRevealer = (key: string) => boolean;

let revealer: RowRevealer | null = null;

/** Список регистрирует показ строки; отдаёт снятие регистрации. */
export function registerRowRevealer(next: RowRevealer): () => void {
  revealer = next;
  return () => {
    if (revealer === next) {
      revealer = null;
    }
  };
}

/**
 * Фокус на строку без прокрутки: экран не должен дёргаться к месту ушедшей
 * строки. Прокрутка — только когда строка не нарисована вовсе.
 */
export function focusNavRow(key: string): boolean {
  const row = findNavRow(key);
  if (row === null) {
    return revealer?.(key) ?? false;
  }
  row.focus({ preventScroll: true });
  return true;
}

/**
 * `finalFocus` панели: после закрытия фокус уходит на соседа скрытой строки,
 * а не на удалённую строку-триггер. Нет отложенной цели — поведение панели по
 * умолчанию.
 */
export function panelFinalFocus(): boolean {
  const key = takePendingHandoffKey();
  if (key === null) {
    return true;
  }
  return !focusNavRow(key);
}
